import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  confirmAccountSelection,
  disconnectIntegration,
  generateAIInsights,
  getClients,
  getIntegrationLogs,
  getIntegrationQuota,
  getIntegrationReadiness,
  getIntegrations,
  getOverlapWarnings,
  getPendingAccountSelection,
  getSubscription,
  startGoogleConnection,
  startLinkedInConnection,
  startMetaConnection,
  startShopifyConnection,
  startTikTokConnection,
  syncIntegration,
  triggerInitialImport,
  updateConnectionSchedule,
} from '../utils/api';

// Common IANA timezones offered in the schedule picker — not exhaustive,
// just a reasonable shortlist; the backend accepts any valid IANA name.
const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const platformDisplayName = {
  meta: 'Meta Ads',
  google_ads: 'Google Ads',
  linkedin_ads: 'LinkedIn Ads',
  shopify: 'Shopify',
  tiktok: 'TikTok Ads',
};

// Shows only the last 4 characters of an external account id — the picker
// deliberately never displays the full raw provider account id to reduce
// what's visible on-screen, while still being enough for an admin to
// disambiguate accounts that share a similar name.
const maskAccountId = (id) => {
  const str = String(id || '');
  if (str.length <= 4) return str;
  return `••••${str.slice(-4)}`;
};

function AccountPickerModal({ token, platform, onDone, onClose }) {
  const [accounts, setAccounts] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    getPendingAccountSelection(token)
      .then((data) => {
        setAccounts(data.accounts || []);
        // Deliberately NOT pre-selecting an account when there's exactly
        // one — the admin must actively choose it, even if there's only
        // one option, per the "never auto-select" requirement. Leaving
        // selectedId empty keeps the Connect button disabled until they do.
      })
      .catch((err) => setError(err.response?.data?.error || 'This selection link is invalid or has expired.'));
  }, [token]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    try {
      setConfirming(true);
      setError('');
      await confirmAccountSelection(token, selectedId);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect the selected account.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16,
    }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 480 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>
          Confirm {platformDisplayName[platform] || 'the'} account
        </h2>
        <p style={{ margin: '6px 0 18px', fontSize: 13, color: '#64748b' }}>
          Review the account details below and confirm it's the correct one for this client before connecting.
          No account is ever bound automatically — this confirmation is always required, even when only one account is available.
        </p>

        {error && (
          <div style={{ marginBottom: 14, padding: 10, borderRadius: 10, background: '#fef2f2', color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}

        {!accounts ? (
          <div style={{ fontSize: 13, color: '#64748b' }}>Loading accounts...</div>
        ) : accounts.length === 0 ? (
          <div style={{ fontSize: 13, color: '#64748b' }}>No accounts found in this selection.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
            {accounts.map((account) => (
              <label
                key={account.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                  border: `1px solid ${selectedId === account.id ? '#2563eb' : '#e2e8f0'}`,
                  borderRadius: 10, cursor: 'pointer',
                  background: selectedId === account.id ? '#eff6ff' : '#fff',
                }}
              >
                <input
                  type="radio"
                  name="account"
                  value={account.id}
                  checked={selectedId === account.id}
                  onChange={() => setSelectedId(account.id)}
                  style={{ marginTop: 3 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{account.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>ID: {maskAccountId(account.id)}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    {account.currency && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#f1f5f9', color: '#475569' }}>
                        {account.currency}
                      </span>
                    )}
                    {account.timezone && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#f1f5f9', color: '#475569' }}>
                        {account.timezone}
                      </span>
                    )}
                    {account.status && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#f1f5f9', color: '#475569' }}>
                        {account.status}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', color: '#334155', padding: '10px 12px', fontWeight: 700, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedId || confirming || !accounts?.length}
            style={{ flex: 1, border: 0, borderRadius: 10, background: '#2563eb', color: '#fff', padding: '10px 12px', fontWeight: 800, cursor: 'pointer', opacity: (!selectedId || confirming) ? 0.7 : 1 }}
          >
            {confirming ? 'Connecting...' : 'Confirm & Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InitialImportPicker({ connection, busy, onImport }) {
  const [range, setRange] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const done = Boolean(connection.initial_sync_completed_at);

  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 12, border: '1px dashed #cbd5e1' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
        {done ? 'Initial import completed' : 'Initial import'}
      </div>
      {done && (
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
          Imported {connection.initial_sync_range_start} to {connection.initial_sync_range_end} on{' '}
          {new Date(connection.initial_sync_completed_at).toLocaleDateString()}. You can re-run it with a different range below.
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[
          { value: '30d', label: 'Last 30 days' },
          { value: '90d', label: 'Last 90 days' },
          { value: 'mtd', label: 'Month to date' },
          { value: 'custom', label: 'Custom' },
        ].map((opt) => (
          <label key={opt.value} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" name={`range-${connection.id}`} checked={range === opt.value} onChange={() => setRange(opt.value)} />
            {opt.label}
          </label>
        ))}
      </div>
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12 }} />
          <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12 }} />
        </div>
      )}
      <button
        onClick={() => onImport(connection, { range, dateFrom: customFrom || undefined, dateTo: customTo || undefined })}
        disabled={busy || (range === 'custom' && (!customFrom || !customTo))}
        style={{ marginTop: 8, width: '100%', border: 0, borderRadius: 8, background: '#0f172a', color: '#fff', padding: '8px 10px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
      >
        {busy ? 'Importing...' : done ? 'Re-run import' : 'Start initial import'}
      </button>
    </div>
  );
}

// `available` here is no longer hardcoded — it's resolved at render time
// from the /integrations/readiness response (booleans only, no
// credentials), so a platform whose server-side app credentials aren't
// configured yet shows "Configuration required" instead of a Connect
// button that would fail partway through (or, worse, appear to work).
const platforms = [
  {
    key: 'meta',
    name: 'Meta Ads',
    description: 'Connect Facebook and Instagram ad accounts.',
  },
  {
    key: 'google_ads',
    name: 'Google Ads',
    description: 'Search, Display and YouTube sync.',
  },
  {
    key: 'linkedin_ads',
    name: 'LinkedIn Ads',
    description: 'B2B campaign sync.',
  },
  {
    key: 'shopify',
    name: 'Shopify',
    description: 'Store revenue and order sync.',
  },
  {
    key: 'tiktok',
    name: 'TikTok Ads',
    description: 'TikTok campaign spend and conversion sync.',
  },
];

const ENABLE_PLATFORM_SYNC = process.env.REACT_APP_ENABLE_PLATFORM_SYNC === 'true';

// UI-only gating for UX (hide the page, show an upgrade message). This is
// never the real security boundary — every API route behind this page
// re-checks the agency's plan server-side and returns a structured 403
// (code: 'feature_not_available') regardless of what this component shows,
// so a Free-plan agency reaching this page via a stale/old link (nav item
// is hidden for them) still can't actually use anything here.
export default function Integrations() {
  const [plan, setPlan] = useState(undefined); // undefined = loading, null = failed to load

  useEffect(() => {
    if (!ENABLE_PLATFORM_SYNC) return;
    getSubscription()
      .then((sub) => setPlan(sub?.plan_name || 'free'))
      .catch(() => setPlan('free'));
  }, []);

  if (!ENABLE_PLATFORM_SYNC) {
    return (
      <div>
        <h1>Integrations</h1>
        <p>Platform integrations are coming soon. Manual uploads and report generation remain available.</p>
      </div>
    );
  }

  if (plan === undefined) {
    return null;
  }

  if (plan === 'free') {
    return (
      <div className="card card-pad" style={{ maxWidth: 560 }}>
        <h1 style={{ fontSize: 20, marginTop: 0 }}>Integrations are a Pro/Agency feature</h1>
        <p style={{ color: 'var(--text2, #64748b)' }}>
          Connecting Meta, Google, LinkedIn, TikTok, or Shopify accounts to sync data automatically requires the Pro
          or Agency plan. Manual CSV/Excel upload and report generation remain available on every plan.
        </p>
        <a href="/subscription" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-block', marginTop: 8 }}>
          View plans
        </a>
      </div>
    );
  }

  return <IntegrationsEnabled plan={plan} />;
}

function IntegrationsEnabled({ plan }) {
  const [clients, setClients] = useState([]);
  // 'loading' | 'ready' | 'error' — kept separate from `clients` itself so
  // an empty array can never be mistaken for "still loading" or "the
  // request failed and we're silently showing nothing." Loading/error/
  // empty are three genuinely different states and must render differently.
  const [clientsStatus, setClientsStatus] = useState('loading');
  const [connections, setConnections] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [activeConnectionId, setActiveConnectionId] = useState('');
  const [loading, setLoading] = useState('');
  const [message, setMessage] = useState('');
  const [quota, setQuota] = useState(null);
  const [readiness, setReadiness] = useState(null);
  // The ONE authoritative signal for whether platform-sync integrations are
  // turned on at all, driven exclusively by the readiness endpoint's own
  // outcome (never by an incidental failure of some other call):
  //   'loading'  — readiness hasn't resolved yet; buttons show a neutral
  //                 "Checking..." state, never "Available" by default.
  //   'ready'    — readiness loaded successfully; real per-platform
  //                 configured/approval booleans drive the cards.
  //   'disabled' — the backend explicitly reported ENABLE_PLATFORM_SYNC is
  //                 off (readiness's own 503, code: 'platform_sync_disabled').
  //                 Renders ONE consistent full-panel disabled state,
  //                 replacing the cards entirely — never a banner sitting
  //                 above still-interactive "Available" cards.
  //   'error'    — readiness failed for some OTHER reason (network, 500,
  //                 auth). Distinct from 'disabled': this is not "the
  //                 feature is off," it's "we don't know," so it must not
  //                 be presented with the same disabled copy, and must not
  //                 fall back to treating platforms as configured.
  const [readinessStatus, setReadinessStatus] = useState('loading');
  const [overlapWarnings, setOverlapWarnings] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadReadiness = () => {
    setReadinessStatus('loading');
    getIntegrationReadiness()
      .then((data) => {
        setReadiness(data);
        setReadinessStatus('ready');
      })
      .catch((error) => {
        const code = error.response?.data?.code;
        setReadiness(null);
        setReadinessStatus(code === 'platform_sync_disabled' ? 'disabled' : 'error');
      });
  };

  useEffect(() => {
    getIntegrationQuota().then(setQuota).catch(() => setQuota(null));
    loadReadiness();
  }, []);

  const pendingSelectToken = searchParams.get('selectAccount');
  const pendingSelectPlatform = searchParams.get('platform');

  const clearPendingSelection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('selectAccount');
    next.delete('platform');
    setSearchParams(next, { replace: true });
  };

  const selectedConnections = useMemo(
    () => connections.filter((connection) => String(connection.client_id) === String(selectedClient)),
    [connections, selectedClient]
  );

  const loadConnections = async () => {
    const data = await getIntegrations();
    setConnections(data || []);
    return data || [];
  };

  // Loads the client list and the connections list as two INDEPENDENT
  // requests — previously these were bundled into a single Promise.all,
  // which meant a failure of either one (e.g. a 503 while platform sync is
  // disabled) silently prevented the OTHER from ever populating, even
  // though the client list itself had already loaded successfully. Clients
  // owned by this agency must always render regardless of whether
  // connections/integrations happen to be available.
  const loadClients = async () => {
    setClientsStatus('loading');
    try {
      const clientRows = await getClients();
      const rows = clientRows || [];
      setClients(rows);
      setClientsStatus('ready');
      if (!selectedClient && rows[0]?.id) {
        setSelectedClient(rows[0].id);
      }
    } catch (error) {
      setClients([]);
      setClientsStatus('error');
    }
  };

  useEffect(() => {
    loadClients();
    loadConnections().catch(() => setConnections([]));
  }, []);

  useEffect(() => {
    const first = selectedConnections[0];
    if (!first) {
      setLogs([]);
      setActiveConnectionId('');
      return;
    }

    setActiveConnectionId(first.id);
    getIntegrationLogs(first.id)
      .then(setLogs)
      .catch(() => setLogs([]));
  }, [selectedConnections]);

  // Overlap warnings are surfaced per selected client — never merged or
  // auto-resolved, purely informational for the admin (see
  // detectSourceOverlaps in syncRunner.js).
  useEffect(() => {
    if (!selectedClient) {
      setOverlapWarnings([]);
      return;
    }
    getOverlapWarnings(selectedClient)
      .then(setOverlapWarnings)
      .catch(() => setOverlapWarnings([]));
  }, [selectedClient, connections]);

  const connectionFor = (platform) =>
    selectedConnections.find((connection) => connection.platform === platform && connection.status !== 'disconnected');

  const startersByPlatform = {
    meta: startMetaConnection,
    google_ads: startGoogleConnection,
    linkedin_ads: startLinkedInConnection,
    tiktok: startTikTokConnection,
  };

  const handleConnect = async (platformKey) => {
    // Client selection is mandatory for every connect action — there is no
    // path to starting an OAuth flow without an explicit client chosen
    // first (a provider login is never treated as an agency-wide data
    // source; every connection is bound to exactly one client).
    if (!selectedClient) {
      setMessage('Please select a client first.');
      return;
    }

    if (platformKey === 'shopify') {
      const shop = window.prompt('Enter Shopify store domain, e.g. your-store.myshopify.com');
      if (!shop) return;
      try {
        setLoading(`${platformKey}-connect`);
        setMessage('');
        const result = await startShopifyConnection(selectedClient, shop);
        if (result.authUrl) window.location.href = result.authUrl;
      } catch (error) {
        setMessage(error.response?.data?.error || 'Shopify connection failed.');
      } finally {
        setLoading('');
      }
      return;
    }

    const starter = startersByPlatform[platformKey];
    if (!starter) return;

    try {
      setLoading(`${platformKey}-connect`);
      setMessage('');
      const result = await starter(selectedClient);
      if (result.authUrl) window.location.href = result.authUrl;
    } catch (error) {
      setMessage(error.response?.data?.error || `${platformDisplayName[platformKey] || platformKey} connection failed.`);
    } finally {
      setLoading('');
    }
  };

  const handleSync = async (connection) => {
    try {
      setLoading(`sync-${connection.id}`);
      setMessage('');
      const result = await syncIntegration(connection.id);
      await generateAIInsights(connection.client_id).catch(() => null);
      await loadConnections();
      setLogs(await getIntegrationLogs(connection.id));
      if (result.quota) setQuota((prev) => ({ ...prev, ...result.quota, unlimited: false }));
      setMessage(`Sync completed. ${result.rowsInserted || 0} inserted, ${result.rowsUpdated || 0} updated, ${result.rowsSkipped || 0} skipped (protected manual-upload rows).`);
    } catch (error) {
      const data = error.response?.data || {};
      if (data.code === 'quota_exceeded') {
        setMessage(data.error || 'Monthly manual sync limit reached for this plan.');
        if (data.quota) setQuota((prev) => ({ ...prev, ...data.quota, unlimited: false }));
      } else if (data.code === 'sync_cooldown' || data.code === 'rate_limited') {
        setMessage(data.error || 'Please wait before syncing again.');
      } else {
        setMessage(data.error || 'Sync failed.');
      }
    } finally {
      setLoading('');
    }
  };

  const handleInitialImport = async (connection, options) => {
    try {
      setLoading(`initial-${connection.id}`);
      setMessage('');
      const result = await triggerInitialImport(connection.id, options);
      await loadConnections();
      setLogs(await getIntegrationLogs(connection.id));
      setMessage(`Initial import completed. ${result.rowsInserted || 0} inserted, ${result.rowsUpdated || 0} updated.`);
    } catch (error) {
      const data = error.response?.data || {};
      setMessage(data.error || 'Initial import failed.');
    } finally {
      setLoading('');
    }
  };

  const handleDisconnect = async (connection, { silent } = {}) => {
    if (!silent) {
      const platformLabel = platformDisplayName[connection.platform] || connection.platform;
      const confirmed = window.confirm(
        `Disconnect ${platformLabel}?\n\nThis revokes the stored connection credentials. Previously imported performance data and reports are NOT deleted and remain fully available.`
      );
      if (!confirmed) return false;
    }

    try {
      setLoading(`disconnect-${connection.id}`);
      setMessage('');
      await disconnectIntegration(connection.id);
      await loadConnections();
      setLogs([]);
      if (!silent) setMessage('Integration disconnected. Previously imported data was kept.');
      return true;
    } catch (error) {
      setMessage(error.response?.data?.error || 'Disconnect failed.');
      return false;
    } finally {
      setLoading('');
    }
  };

  // "Change Account" replaces which external account a client's connection
  // points to for a platform. There is no in-place account swap on the
  // backend (the unique mapping is keyed by the specific external account
  // id) — the correct, explicit sequence is: disconnect the current
  // mapping (which preserves its historical performance_data rows, per
  // the disconnect route), then start a fresh, fully-confirmed OAuth
  // connect for the same platform/client.
  const handleChangeAccount = async (connection) => {
    const platformLabel = platformDisplayName[connection.platform] || connection.platform;
    const confirmed = window.confirm(
      `Change the ${platformLabel} account for this client?\n\nThe current connection will be disconnected (historical data is kept) and you'll be sent through a new account-confirmation flow.`
    );
    if (!confirmed) return;

    const disconnected = await handleDisconnect(connection, { silent: true });
    if (disconnected) {
      await handleConnect(connection.platform);
    }
  };

  // Agency-only — the backend independently rejects this for Pro/Free
  // (assertAutoSyncAllowed), this is just the matching UI affordance.
  const handleScheduleUpdate = async (connection, patch) => {
    try {
      setLoading(`schedule-${connection.id}`);
      setMessage('');
      await updateConnectionSchedule(connection.id, patch);
      await loadConnections();
      setMessage('Automatic sync schedule updated.');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to update schedule.');
    } finally {
      setLoading('');
    }
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleString() : 'Not synced yet');

  const clientOverlapWarnings = overlapWarnings.filter((w) => String(w.client_id) === String(selectedClient));

  return (
    <div>
      {pendingSelectToken && (
        <AccountPickerModal
          token={pendingSelectToken}
          platform={pendingSelectPlatform}
          onClose={clearPendingSelection}
          onDone={async () => {
            clearPendingSelection();
            await loadConnections();
            setMessage('Account connected successfully.');
          }}
        />
      )}

      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>
          Integrations
        </h1>
        <p style={{ color: '#64748b', marginTop: 6, fontSize: 13 }}>
          Connect client platforms and sync imported metrics into the same reporting engine used by manual uploads.
          Every connection is bound to exactly one client and one external account.
        </p>
      </div>

      {plan === 'pro' && quota && !quota.unlimited && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 14, padding: '14px 18px', marginBottom: 18, fontSize: 13, color: '#92400e' }}>
          <strong>Manual sync quota:</strong> {quota.used} of {quota.limit} used this month
          {' '}&middot;{' '} {quota.remaining} remaining
          {' '}&middot;{' '} resets {quota.resetsAt ? new Date(quota.resetsAt).toLocaleDateString() : '—'}.
          {' '}Automatic recurring sync is not included on the Pro plan.
        </div>
      )}

      {plan === 'agency' && quota && quota.unlimited && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 14, padding: '14px 18px', marginBottom: 18, fontSize: 13, color: '#1d4ed8' }}>
          <strong>Agency plan:</strong> connected accounts can sync automatically on a daily, weekly, or monthly
          schedule, plus on-demand manual sync (a short cooldown applies between manual syncs on the same connection).
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
          Client <span style={{ color: '#dc2626' }}>*</span> (required before connecting any platform)
        </label>

        {clientsStatus === 'loading' && (
          <div style={{ fontSize: 13, color: '#64748b', padding: '10px 0' }}>Loading clients...</div>
        )}

        {clientsStatus === 'error' && (
          <div style={{ padding: 12, borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
            Couldn't load your clients. This is not the same as having no clients — please retry.
            <button
              onClick={loadClients}
              style={{ marginLeft: 10, border: '1px solid #fecaca', borderRadius: 8, background: '#fff', color: '#dc2626', padding: '4px 10px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
            >
              Retry
            </button>
          </div>
        )}

        {clientsStatus === 'ready' && clients.length === 0 && (
          <div style={{ padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 13 }}>
            No active clients yet. <a href="/clients">Add a client</a> before connecting a platform.
          </div>
        )}

        {clientsStatus === 'ready' && clients.length > 0 && (
          <select
            value={selectedClient}
            onChange={(event) => setSelectedClient(event.target.value)}
            style={{ width: '100%', maxWidth: 420, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 10 }}
          >
            <option value="">Select Client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        )}

        {message && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 600 }}>
            {message}
          </div>
        )}

        {clientOverlapWarnings.length > 0 && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12 }}>
            <strong>Data overlap notice:</strong> this client has both manually-uploaded and platform-synced data for
            the same month/platform. Synced data never overwrites manual uploads, but totals for the overlapping
            month(s) may double-count if both are included in the same report. Affected periods:
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {clientOverlapWarnings.map((w) => (
                <li key={`${w.platform}-${w.report_month}`}>
                  {platformDisplayName[w.platform] || w.platform} — {w.report_month} ({w.manual_rows} manual, {w.sync_rows} synced)
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {readinessStatus === 'disabled' && (
        // Platform sync is genuinely, deliberately off (ENABLE_PLATFORM_SYNC
        // is false server-side) — ONE consistent full-panel state replaces
        // the entire platform grid. There is no world in which this
        // co-exists with cards showing "Available"; that combination was
        // exactly the reported bug, caused by unrelated request failures
        // being misread as "disabled" while OTHER calls silently defaulted
        // to treating everything as available.
        <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 16, padding: 32, textAlign: 'center', color: '#64748b' }}>
          <h3 style={{ margin: '0 0 6px', color: '#334155' }}>Platform integrations are temporarily disabled</h3>
          <p style={{ margin: 0, fontSize: 13 }}>Data accuracy validation is in progress. Manual uploads and report generation remain available.</p>
        </div>
      )}

      {readinessStatus === 'error' && (
        // Distinct from 'disabled': we don't actually know whether the
        // feature is on or off (a network/auth/server error prevented us
        // from asking) — showing the "temporarily disabled" copy here would
        // be a false claim, and showing live cards would risk a hardcoded
        // "Available" the same way the previous bug did. Block, don't guess.
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 16, padding: 32, textAlign: 'center', color: '#dc2626' }}>
          <h3 style={{ margin: '0 0 6px' }}>Couldn't determine integration status</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13 }}>We weren't able to check which platforms are configured. Connecting is disabled until this is resolved.</p>
          <button
            onClick={loadReadiness}
            style={{ border: '1px solid #fecaca', borderRadius: 8, background: '#fff', color: '#dc2626', padding: '8px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
          >
            Retry
          </button>
        </div>
      )}

      {(readinessStatus === 'loading' || readinessStatus === 'ready') && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        {platforms.map((platform) => {
          const connection = connectionFor(platform.key);
          const busy = loading.includes(platform.key) || loading.includes(connection?.id);
          const platformReadiness = readiness?.platforms?.[platform.key];
          const stillChecking = readinessStatus === 'loading';
          // Real values only once readiness has actually loaded — never
          // defaulted to true/"Available" while loading or on failure.
          const configured = !stillChecking && Boolean(platformReadiness?.configured);
          const requiresApproval = !stillChecking && Boolean(platformReadiness?.requiresProviderApproval);
          const tokenEncryptionConfigured = stillChecking ? false : Boolean(readiness?.tokenEncryptionConfigured);

          // Distinct states, per connection.status (connected / expired / failed) or absence of a connection:
          //   checking       -> "Checking..." (readiness not loaded yet — never a default "Available")
          //   not connected, not configured           -> Configuration required
          //   not connected, configured, needs approval -> App approval required (provider-side, not detectable by us)
          //   not connected, configured, no approval needed -> Available
          //   connected      -> Connected
          //   expired        -> Reconnect (token expired, needs a fresh OAuth round-trip)
          //   failed         -> Connection Error, with a Reconnect action
          let stateLabel = 'Checking...';
          let stateColors = { bg: '#f1f5f9', fg: '#94a3b8' };
          if (connection) {
            if (connection.status === 'connected') {
              stateLabel = 'Connected';
              stateColors = { bg: '#dcfce7', fg: '#15803d' };
            } else if (connection.status === 'expired') {
              stateLabel = 'Reconnect needed';
              stateColors = { bg: '#fef3c7', fg: '#b45309' };
            } else if (connection.status === 'failed') {
              stateLabel = 'Connection error';
              stateColors = { bg: '#fee2e2', fg: '#dc2626' };
            } else {
              stateLabel = connection.status;
            }
          } else if (stillChecking) {
            stateLabel = 'Checking...';
            stateColors = { bg: '#f1f5f9', fg: '#94a3b8' };
          } else if (!configured) {
            // Distinct from a real misconfiguration: this platform's OAuth
            // app credentials were never provisioned because it's not on the
            // launch roadmap yet, not because something needs fixing.
            stateLabel = 'Coming soon';
            stateColors = { bg: '#ede9fe', fg: '#6d28d9' };
          } else if (requiresApproval) {
            stateLabel = 'App approval required';
            stateColors = { bg: '#fef3c7', fg: '#b45309' };
          } else {
            stateLabel = 'Available';
            stateColors = { bg: '#dbeafe', fg: '#1d4ed8' };
          }

          const needsReconnect = connection && (connection.status === 'expired' || connection.status === 'failed');
          const isAgencyConnection = plan === 'agency' && connection;
          const frequency = connection?.sync_frequency || 'manual';

          return (
            <div key={platform.key} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>{platform.name}</h3>
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>{platform.description}</p>
                  {!connection && requiresApproval && (
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#b45309' }}>
                      Requires provider app approval in addition to configuration.
                    </p>
                  )}
                </div>
                <span style={{
                  height: 24,
                  padding: '4px 9px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  background: stateColors.bg,
                  color: stateColors.fg,
                }}>
                  {stateLabel}
                </span>
              </div>

              {connection ? (
                <div style={{ marginTop: 16 }}>
                  <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12, fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
                    <div><strong>Account:</strong> {connection.account_name || connection.account_id} ({maskAccountId(connection.account_id)})</div>
                    {(connection.account_currency || connection.account_timezone) && (
                      <div>
                        <strong>Details:</strong> {[connection.account_currency, connection.account_timezone].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    <div><strong>Last successful sync:</strong> {formatDate(connection.last_success_at)}</div>
                    <div><strong>Last attempt:</strong> {formatDate(connection.last_sync_at)}</div>
                    {isAgencyConnection && connection.auto_sync_enabled && (
                      <div><strong>Next scheduled sync:</strong> {formatDate(connection.next_sync_at)} ({frequency})</div>
                    )}
                    {logs[0] && String(logs[0].connection_id) === String(connection.id) && logs[0].actual_date_start && (
                      <div><strong>Last synced range:</strong> {logs[0].actual_date_start} to {logs[0].actual_date_end}</div>
                    )}
                    {connection.last_error && (
                      <div style={{ color: '#dc2626' }}><strong>Last error:</strong> {connection.last_error}</div>
                    )}
                  </div>

                  <InitialImportPicker connection={connection} busy={loading === `initial-${connection.id}`} onImport={handleInitialImport} />

                  {isAgencyConnection && (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 12, border: '1px dashed #cbd5e1' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#334155', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(connection.auto_sync_enabled)}
                          disabled={busy}
                          onChange={(e) => handleScheduleUpdate(connection, { autoSyncEnabled: e.target.checked })}
                        />
                        Automatic sync
                      </label>
                      {connection.auto_sync_enabled && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <select
                            value={frequency === 'manual' ? 'daily' : frequency}
                            disabled={busy}
                            onChange={(e) => {
                              const nextFrequency = e.target.value;
                              const patch = { syncFrequency: nextFrequency };
                              if (nextFrequency === 'weekly') patch.dayOfWeek = connection.sync_day_of_week ?? 1;
                              if (nextFrequency === 'monthly') patch.dayOfMonth = connection.sync_day_of_month ?? 1;
                              handleScheduleUpdate(connection, patch);
                            }}
                            style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12 }}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>

                          {frequency === 'weekly' && (
                            <select
                              value={connection.sync_day_of_week ?? 1}
                              disabled={busy}
                              onChange={(e) => handleScheduleUpdate(connection, { syncFrequency: 'weekly', dayOfWeek: Number(e.target.value) })}
                              style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12 }}
                            >
                              {WEEKDAY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </select>
                          )}

                          {frequency === 'monthly' && (
                            <select
                              value={connection.sync_day_of_month ?? 1}
                              disabled={busy}
                              onChange={(e) => handleScheduleUpdate(connection, { syncFrequency: 'monthly', dayOfMonth: Number(e.target.value) })}
                              style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12 }}
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Day {d}</option>)}
                            </select>
                          )}

                          <div style={{ display: 'flex', gap: 8 }}>
                            <select
                              value={connection.preferred_sync_hour ?? 6}
                              disabled={busy}
                              onChange={(e) => handleScheduleUpdate(connection, { preferredSyncHour: Number(e.target.value) })}
                              style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12 }}
                            >
                              {Array.from({ length: 24 }, (_, h) => (
                                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                              ))}
                            </select>
                            <select
                              value={connection.timezone || 'UTC'}
                              disabled={busy}
                              onChange={(e) => handleScheduleUpdate(connection, { timezone: e.target.value })}
                              style={{ flex: 1, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12 }}
                            >
                              {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => (needsReconnect ? handleConnect(platform.key) : handleSync(connection))}
                      disabled={busy}
                      style={{ flex: 1, minWidth: 100, border: 0, borderRadius: 10, background: needsReconnect ? '#b45309' : '#2563eb', color: '#fff', padding: '10px 12px', fontWeight: 800, cursor: 'pointer' }}
                    >
                      {busy ? 'Working...' : needsReconnect ? 'Reconnect' : 'Sync Now'}
                    </button>
                    <button
                      onClick={() => handleChangeAccount(connection)}
                      disabled={busy}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', color: '#334155', padding: '10px 12px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      Change Account
                    </button>
                    <button
                      onClick={() => handleDisconnect(connection)}
                      disabled={busy}
                      style={{ border: '1px solid #fecaca', borderRadius: 10, background: '#fff', color: '#dc2626', padding: '10px 12px', fontWeight: 800, cursor: 'pointer' }}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ) : (
                (() => {
                  // TOKEN_ENCRYPTION_KEY is required before ANY OAuth
                  // connection can start (tokens can't be stored safely
                  // without it) — this blocks every Connect button
                  // regardless of that individual platform's own
                  // credentials, with its own explicit message, per the
                  // requirement that a missing encryption key must never be
                  // silently indistinguishable from "platform not configured."
                  const canConnect = !stillChecking && tokenEncryptionConfigured && configured && Boolean(selectedClient);
                  let label = `Connect ${platform.name}`;
                  let title;
                  if (stillChecking) {
                    label = 'Checking...';
                  } else if (!tokenEncryptionConfigured) {
                    label = 'Server configuration required';
                    title = 'Integrations are not fully configured on this server yet (missing token encryption key).';
                  } else if (!configured) {
                    label = 'Coming soon';
                    title = `${platform.name} integration is on the roadmap and not available to connect yet.`;
                  } else if (requiresApproval) {
                    label = `Connect ${platform.name}`;
                    title = 'Configured, but this provider still requires app-review approval before real accounts can be connected.';
                  } else if (!selectedClient) {
                    label = 'Select a client first';
                    title = 'Select a client first.';
                  }

                  return (
                    <button
                      onClick={() => handleConnect(platform.key)}
                      disabled={!canConnect || busy}
                      title={title}
                      style={{
                        width: '100%',
                        marginTop: 18,
                        border: 0,
                        borderRadius: 10,
                        background: canConnect ? '#0f172a' : '#e2e8f0',
                        color: canConnect ? '#fff' : '#64748b',
                        padding: '10px 12px',
                        fontWeight: 800,
                        cursor: canConnect ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {label}
                    </button>
                  );
                })()
              )}
            </div>
          );
        })}
      </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 18, marginTop: 18 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Sync Logs</h2>
        <div style={{ marginTop: 14 }}>
          {!activeConnectionId || logs.length === 0 ? (
            <div style={{ padding: 18, border: '1px dashed #cbd5e1', borderRadius: 12, color: '#64748b', fontSize: 13 }}>
              No sync logs yet.
            </div>
          ) : (
            logs.map((log) => (
              <div key={log.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <div>
                  <strong style={{ color: log.status === 'failed' ? '#dc2626' : '#0f172a' }}>{log.status}</strong>
                  <span style={{ color: '#64748b' }}>
                    {' '}· {log.sync_type} · fetched {log.rows_fetched || 0}, inserted {log.rows_inserted || 0}, updated {log.rows_updated || 0}, skipped {log.rows_skipped || 0}
                  </span>
                  {(log.requested_date_start || log.actual_date_start) && (
                    <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                      Requested {log.requested_date_start || '—'} to {log.requested_date_end || '—'}
                      {log.actual_date_start ? ` · Actual ${log.actual_date_start} to ${log.actual_date_end}` : ''}
                    </div>
                  )}
                  {log.error_message && <div style={{ color: '#dc2626', marginTop: 4 }}>{log.error_message}</div>}
                </div>
                <span style={{ color: '#94a3b8' }}>{formatDate(log.created_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
